import { Directive, ElementRef, HostBinding, HostListener, inject, output } from '@angular/core';

export interface IPanMoveEvent {
  deltaX: number;
  width: number;
}

export interface IPanEndEvent {
  deltaX: number;
  width: number;
  velocity: number;
}

/**
 * Reusable horizontal pan/swipe gesture based on Pointer Events (works for mouse and touch alike).
 * Locks onto the horizontal axis only after the pointer clearly moves horizontally, so vertical
 * page scrolling keeps working. Consumers decide what to do with the emitted deltas.
 */
@Directive({
  selector: '[gcPan]',
  standalone: true,
})
export class PanDirective {
  public panStart = output<void>();

  public panMove = output<IPanMoveEvent>();

  public panEnd = output<IPanEndEvent>();

  @HostBinding('style.touch-action')
  public touchAction = 'pan-y';

  private static readonly AXIS_LOCK_THRESHOLD = 8;

  private el = inject<ElementRef<HTMLElement>>(ElementRef);

  private pointerId: number | null = null;

  private startX = 0;

  private startY = 0;

  private lastX = 0;

  private velocity = 0;

  private horizontal = false;

  @HostListener('pointerdown', ['$event'])
  public onPointerDown(event: PointerEvent): void {
    if (!event.isPrimary || this.pointerId !== null) {
      return;
    }

    this.pointerId = event.pointerId;
    this.startX = this.lastX = event.clientX;
    this.startY = event.clientY;
    this.velocity = 0;
    this.horizontal = false;
  }

  @HostListener('pointermove', ['$event'])
  public onPointerMove(event: PointerEvent): void {
    if (event.pointerId !== this.pointerId) {
      return;
    }

    const deltaX = event.clientX - this.startX;
    const deltaY = event.clientY - this.startY;

    if (!this.horizontal) {
      if (Math.abs(deltaX) < PanDirective.AXIS_LOCK_THRESHOLD && Math.abs(deltaY) < PanDirective.AXIS_LOCK_THRESHOLD) {
        return;
      }

      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        // Vertical intent - release the gesture and let the page scroll.
        this.pointerId = null;
        return;
      }

      this.horizontal = true;
      this.el.nativeElement.setPointerCapture(event.pointerId);
      this.panStart.emit();
    }

    this.velocity = event.clientX - this.lastX;
    this.lastX = event.clientX;

    this.panMove.emit({ deltaX, width: this.el.nativeElement.clientWidth });
  }

  @HostListener('pointerup', ['$event'])
  @HostListener('pointercancel', ['$event'])
  public onPointerUp(event: PointerEvent): void {
    if (event.pointerId !== this.pointerId) {
      return;
    }

    if (this.horizontal) {
      this.panEnd.emit({
        deltaX: event.clientX - this.startX,
        width: this.el.nativeElement.clientWidth,
        velocity: this.velocity,
      });
    }

    this.pointerId = null;
    this.horizontal = false;
  }
}
